import { useState, useRef, useCallback, useEffect } from "react";

const STORAGE_PREFIX = "modal_position_";

/**
 * Hook to make MUI Dialog components draggable on desktop.
 * Returns position state and handlers to attach to the dialog's title/header area.
 *
 * @param {boolean} enabled - Whether dragging is enabled (typically !isMobile)
 * @param {boolean} isOpen - Whether the modal is open
 * @param {string} storageKey - Unique key to persist position in localStorage (optional)
 * @returns {Object} - { position, handleMouseDown, resetPosition, isDragging }
 */
const useDraggable = (enabled = true, isOpen = true, storageKey = null) => {
  const [position, setPosition] = useState(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(STORAGE_PREFIX + storageKey);
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }
    return { x: 0, y: 0 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0,
  });

  // Load saved position when modal opens
  useEffect(() => {
    if (isOpen && storageKey) {
      try {
        const saved = localStorage.getItem(STORAGE_PREFIX + storageKey);
        if (saved) {
          setPosition(JSON.parse(saved));
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [isOpen, storageKey]);

  const handleMouseDown = useCallback(
    (e) => {
      if (!enabled) return;

      // Only allow dragging from header area (not buttons/inputs inside it)
      if (
        e.target.tagName === "BUTTON" ||
        e.target.tagName === "INPUT" ||
        e.target.closest("button") ||
        e.target.closest("input")
      ) {
        return;
      }

      e.preventDefault();
      setIsDragging(true);

      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        initialX: position.x,
        initialY: position.y,
      };

      const handleMouseMove = (moveEvent) => {
        const deltaX = moveEvent.clientX - dragRef.current.startX;
        const deltaY = moveEvent.clientY - dragRef.current.startY;

        const newPosition = {
          x: dragRef.current.initialX + deltaX,
          y: dragRef.current.initialY + deltaY,
        };
        setPosition(newPosition);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        // Save position to localStorage
        if (storageKey) {
          setPosition((currentPos) => {
            try {
              localStorage.setItem(
                STORAGE_PREFIX + storageKey,
                JSON.stringify(currentPos)
              );
            } catch (e) {
              // Ignore localStorage errors
            }
            return currentPos;
          });
        }
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [enabled, position, storageKey]
  );

  const resetPosition = useCallback(() => {
    setPosition({ x: 0, y: 0 });
    if (storageKey) {
      try {
        localStorage.removeItem(STORAGE_PREFIX + storageKey);
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [storageKey]);

  // Generate transform style for the dialog Paper
  const getDialogStyle = useCallback(() => {
    if (!enabled || (position.x === 0 && position.y === 0)) {
      return {};
    }
    return {
      transform: `translate(${position.x}px, ${position.y}px)`,
    };
  }, [enabled, position]);

  // Style for the draggable header area
  const getDragHandleStyle = useCallback(() => {
    if (!enabled) return {};
    return {
      cursor: isDragging ? "grabbing" : "grab",
      userSelect: "none",
    };
  }, [enabled, isDragging]);

  return {
    position,
    handleMouseDown,
    resetPosition,
    isDragging,
    getDialogStyle,
    getDragHandleStyle,
  };
};

export default useDraggable;
